import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Utensils, AlertTriangle, ThumbsUp, Minus, Info } from 'lucide-react';
import { logError } from '../lib/errorLogger';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMPACT_CONFIG = {
  high_trigger: { label: 'Major Trigger', color: '#A388EE', bg: 'bg-[#A388EE]', textBg: 'bg-[#A388EE]/15 text-[#A388EE]', icon: <AlertTriangle className="w-3 h-3" /> },
  moderate_trigger: { label: 'Possible Trigger', color: '#E5C07B', bg: 'bg-[#E5C07B]', textBg: 'bg-[#E5C07B]/20 text-[#9A7B2E]', icon: <AlertTriangle className="w-3 h-3" /> },
  neutral: { label: 'Neutral', color: '#8C7A76', bg: 'bg-[#8C7A76]', textBg: 'bg-[#FFF2EC] text-[#8C7A76]', icon: <Minus className="w-3 h-3" /> },
  slightly_beneficial: { label: 'Helpful', color: '#4ADE80', bg: 'bg-[#4ADE80]', textBg: 'bg-[#4ADE80]/15 text-[#FF6B4A]', icon: <ThumbsUp className="w-3 h-3" /> },
  beneficial: { label: 'Great for Skin', color: '#FF6B4A', bg: 'bg-[#FF6B4A]', textBg: 'bg-[#FF6B4A]/15 text-[#FF6B4A]', icon: <ThumbsUp className="w-3 h-3" /> },
};

const HeatBar = ({ value, max, color }) => {
  const width = Math.min(100, Math.max(5, (value / max) * 100));
  return (
    <div className="h-2.5 bg-[#FFF2EC] rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${width}%`, backgroundColor: color }} />
    </div>
  );
};

const FoodRow = ({ trigger, maxAcne }) => {
  const config = IMPACT_CONFIG[trigger.impact] || IMPACT_CONFIG.neutral;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-[#FFF2EC] last:border-0" data-testid={`food-trigger-${trigger.food.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-[110px] flex-shrink-0">
        <p className="text-sm font-medium text-[#1A1514] truncate">{trigger.food}</p>
        <p className="text-[10px] text-[#8C7A76]">{trigger.count}x logged</p>
      </div>
      <div className="flex-1">
        <HeatBar value={trigger.avg_acne} max={maxAcne} color={config.color} />
      </div>
      <div className="w-10 text-right">
        <span className="text-sm font-mono font-semibold" style={{ color: config.color }}>{trigger.avg_acne}</span>
      </div>
      <div className="w-[100px] flex-shrink-0">
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${config.textBg}`}>
          {config.icon} {config.label}
        </span>
      </div>
    </div>
  );
};

const TRIGGER_IMPACTS = ['high_trigger', 'moderate_trigger'];
const SAFE_IMPACTS = ['beneficial', 'slightly_beneficial'];

const filterTriggers = (triggers, filter) => {
  if (filter === 'triggers') return triggers.filter((t) => TRIGGER_IMPACTS.includes(t.impact));
  if (filter === 'safe') return triggers.filter((t) => SAFE_IMPACTS.includes(t.impact));
  return triggers;
};

const countByImpact = (triggers, impacts) => triggers.filter((t) => impacts.includes(t.impact)).length;

const EmptyState = () => (
  <Card className="card-elevated border-0" data-testid="food-trigger-heatmap">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-[#1A1514]">
        <Utensils className="w-5 h-5 text-[#A388EE]" />
        Food Trigger Heatmap
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-center py-6">
        <Utensils className="w-10 h-10 mx-auto text-[#E5D0C9] mb-3" />
        <p className="text-sm text-[#8C7A76]">Log your food intake for a few days to see which foods affect your skin.</p>
      </div>
    </CardContent>
  </Card>
);

const FilterChips = ({ filter, setFilter, total, triggerCount, safeCount }) => (
  <div className="flex gap-2 mb-4">
    <button onClick={() => setFilter('all')}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === 'all' ? 'bg-[#FF6B4A] text-white' : 'bg-[#FFF2EC] text-[#8C7A76] hover:bg-[#E5D0C9]'}`}
      data-testid="filter-all">
      All ({total})
    </button>
    <button onClick={() => setFilter('triggers')}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === 'triggers' ? 'bg-[#A388EE] text-white' : 'bg-[#FFF2EC] text-[#8C7A76] hover:bg-[#E5D0C9]'}`}
      data-testid="filter-triggers">
      Triggers ({triggerCount})
    </button>
    <button onClick={() => setFilter('safe')}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === 'safe' ? 'bg-[#FF6B4A] text-white' : 'bg-[#FFF2EC] text-[#8C7A76] hover:bg-[#E5D0C9]'}`}
      data-testid="filter-safe">
      Skin-Friendly ({safeCount})
    </button>
  </div>
);

const FoodTriggerHeatmap = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchTriggers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/food-triggers`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      logError('FoodTriggerHeatmap', 'Failed to fetch food triggers', { error: err?.message });
    }
  }, []);

  useEffect(() => {
    fetchTriggers();
  }, [fetchTriggers]);

  if (!data || data.triggers.length === 0) return <EmptyState />;

  const filteredTriggers = filterTriggers(data.triggers, filter);
  const maxAcne = Math.max(10, ...data.triggers.map((t) => t.avg_acne));

  return (
    <Card className="card-elevated border-0" data-testid="food-trigger-heatmap">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-[#1A1514]">
            <Utensils className="w-5 h-5 text-[#A388EE]" />
            Food Trigger Heatmap
          </CardTitle>
          <span className="text-xs text-[#8C7A76]">{data.days_analyzed} days analyzed</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-4 p-3 bg-[#FFF2EC] rounded-lg">
          <Info className="w-4 h-4 text-[#8C7A76] flex-shrink-0" />
          <p className="text-xs text-[#8C7A76]">
            Your average acne is <span className="font-semibold text-[#1A1514]">{data.baseline_acne}/10</span>. 
            Foods above this line may be triggers. Foods below may help your skin.
          </p>
        </div>

        <FilterChips
          filter={filter} setFilter={setFilter}
          total={data.triggers.length}
          triggerCount={countByImpact(data.triggers, TRIGGER_IMPACTS)}
          safeCount={countByImpact(data.triggers, SAFE_IMPACTS)}
        />

        <div className="flex items-center gap-3 mb-2 px-1">
          <span className="w-[110px] text-[10px] text-[#8C7A76] uppercase tracking-wider">Food</span>
          <span className="flex-1 text-[10px] text-[#8C7A76] uppercase tracking-wider">Avg Acne When Eaten</span>
          <span className="w-10 text-[10px] text-[#8C7A76] uppercase tracking-wider text-right">Score</span>
          <span className="w-[100px] text-[10px] text-[#8C7A76] uppercase tracking-wider">Impact</span>
        </div>

        <div className="max-h-[300px] overflow-y-auto">
          {filteredTriggers.map((trigger) => (
            <FoodRow key={trigger.food} trigger={trigger} maxAcne={maxAcne} />
          ))}
          {filteredTriggers.length === 0 && (
            <p className="text-sm text-[#8C7A76] text-center py-6">
              {filter === 'triggers' ? 'No food triggers detected yet. Keep logging!' : 'No skin-friendly foods identified yet.'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FoodTriggerHeatmap;
